import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  useWindowDimensions,
} from 'react-native';
import { Helmet } from 'react-helmet-async';
import { useAppTheme } from '../../utils/useAppTheme';
import {
  getLocalizedBiography,
  getPersonDetails,
  searchPeople,
} from '../../utils/famousPeopleService';

// Default suggested people - relevant to gematria, numerology, kabbalah
const SUGGESTED_PEOPLE = [
  { id: 'Q317521', name: 'Elon Musk', description: 'Business magnate and engineer' },
  { id: 'Q22686', name: 'Donald Trump', description: '45th and 47th president of the United States' },
  { id: 'Q9036', name: 'Nikola Tesla', description: 'Inventor and electrical engineer (1856-1943)' },
  { id: 'Q172684', name: 'Aleister Crowley', description: 'Occultist and ceremonial magician (1875-1947)' },
  { id: 'Q40662', name: 'King Solomon', description: 'King of Israel, central figure in Kabbalah' },
  { id: 'Q10261', name: 'Pythagoras', description: 'Philosopher and father of numerology (c. 570-495 BC)' },
];

export default function FamousPeoplePage() {
  const { colors } = useAppTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors, isMobile), [colors, isMobile]);

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingBiography, setLoadingBiography] = useState(false);
  const searchTimeout = useRef(null);
  const biographyRequestId = useRef(0);

  // Debounced search
  const handleSearchChange = useCallback((text) => {
    setSearchQuery(text);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (text.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      const people = await searchPeople(text);
      setResults(people);
      setSearching(false);
    }, 500);
  }, []);

  // Select a person from search results
  const handleSelectPerson = useCallback(async (person) => {
    const requestId = ++biographyRequestId.current;
    setLoadingDetails(true);
    setLoadingBiography(false);
    setSelectedPerson(null);

    try {
      const details = await getPersonDetails(person.id);
      if (requestId === biographyRequestId.current && details) {
        setSelectedPerson(details);
      }
    } finally {
      if (requestId === biographyRequestId.current) {
        setLoadingDetails(false);
      }
    }
  }, []);

  // Navigate back to search results
  const handleBack = useCallback(() => {
    biographyRequestId.current += 1;
    setLoadingBiography(false);
    setSelectedPerson(null);
  }, []);

  const handleBiographyLanguage = useCallback(async (language) => {
    if (!selectedPerson || loadingBiography) return;
    if (language.code === selectedPerson.biographyLanguage) return;

    const requestId = ++biographyRequestId.current;
    setLoadingBiography(true);

    try {
      const localized = await getLocalizedBiography(language.code, language.title);
      if (requestId !== biographyRequestId.current || !localized) return;

      setSelectedPerson(current => (
        current ? { ...current, ...localized } : current
      ));
    } finally {
      if (requestId === biographyRequestId.current) {
        setLoadingBiography(false);
      }
    }
  }, [selectedPerson, loadingBiography]);

  // Render a single paragraph with bold/italic segments
  const renderRichParagraph = (segments, paraIndex) => {
    if (!segments || segments.length === 0) return null;
    return (
      <Text
        key={paraIndex}
        style={[
          styles.bioText,
          ['ar', 'he'].includes(selectedPerson?.biographyLanguage) && styles.bioTextRtl,
        ]}
      >
        {segments.map((seg, i) => {
          const segStyle = [];
          if (seg.bold) segStyle.push(styles.bioBold);
          if (seg.italic) segStyle.push(styles.bioItalic);
          return (
            <Text key={i} style={segStyle.length > 0 ? segStyle : undefined}>
              {seg.text}
            </Text>
          );
        })}
      </Text>
    );
  };

  // Render the detail view for a selected person
  const renderPersonDetail = () => {
    if (!selectedPerson) return null;

    const {
      name,
      description,
      dob,
      dod,
      occupations,
      biographyBlocks,
      biographyLanguage,
      wikipediaLanguages,
      timeline,
    } = selectedPerson;

    return (
      <ScrollView style={styles.detailContainer} showsVerticalScrollIndicator={false}>
        {/* Back button */}
        <TouchableOpacity style={styles.backButton} onPress={handleBack} accessibilityLabel="Back to results">
          <Text style={styles.backText}>← Back to results</Text>
        </TouchableOpacity>

        {/* Name & Description */}
        <Text style={styles.personName}>{name}</Text>
        {description ? <Text style={styles.personDescription}>{description}</Text> : null}
        {occupations.length > 0 && (
          <Text style={styles.occupations}>{occupations.join(' • ')}</Text>
        )}

        {/* DOB / DOD Cards */}
        <View style={styles.datesContainer}>
          {/* DOB Card */}
          <View style={[styles.dateCard, styles.dobCard]}>
            <Text style={styles.dateCardLabel}>BORN</Text>
            <Text style={styles.dateCardValue}>
              {dob?.formatted || 'Unknown'}
            </Text>
          </View>

          {/* DOD Card */}
          <View style={[styles.dateCard, styles.dodCard]}>
            <Text style={[styles.dateCardLabel, !dod && { color: '#4CAF50' }]}>
              {dod ? 'DIED' : 'LIVING'}
            </Text>
            <Text style={styles.dateCardValue}>
              {dod?.formatted || 'Still alive'}
            </Text>
          </View>
        </View>

        {/* Timeline */}
        {timeline && timeline.length > 0 && (
          <View style={styles.timelineSection}>
            <Text style={styles.sectionTitle}>Timeline</Text>
            {timeline.map((event, index) => (
              <View key={index} style={styles.timelineItem}>
                <View style={styles.timelineDotColumn}>
                  <View style={[
                    styles.timelineDot,
                    event.isDob && styles.timelineDotDob,
                    event.isDod && styles.timelineDotDod,
                  ]} />
                  {index < timeline.length - 1 && <View style={styles.timelineLine} />}
                </View>
                <View style={styles.timelineContent}>
                  <Text style={[
                    styles.timelineDate,
                    (event.isDob || event.isDod) && styles.timelineDateHighlight,
                  ]}>
                    {event.displayDate}
                  </Text>
                  {event.event ? (
                    <Text style={styles.timelineEvent}>{event.event}</Text>
                  ) : null}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Biography */}
        {(biographyBlocks?.length > 0 || wikipediaLanguages?.length > 0) && (
          <View style={styles.bioSection}>
            <Text style={styles.sectionTitle}>Biography</Text>

            {wikipediaLanguages?.length > 1 && (
              <View style={styles.languageSection}>
                <View style={styles.languageHeader}>
                  <Text style={styles.languageLabel}>Wikipedia language</Text>
                  {loadingBiography && (
                    <ActivityIndicator size="small" color={colors.primary} />
                  )}
                </View>
                <View style={styles.languageList}>
                  {wikipediaLanguages.map(language => {
                    const isActive = language.code === biographyLanguage;
                    return (
                      <TouchableOpacity
                        key={language.code}
                        style={[
                          styles.languageChip,
                          isActive && styles.languageChipActive,
                        ]}
                        onPress={() => handleBiographyLanguage(language)}
                        disabled={loadingBiography || isActive}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={`Read biography in ${language.label}`}
                      >
                        <Text style={[
                          styles.languageChipText,
                          isActive && styles.languageChipTextActive,
                        ]}>
                          {language.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}

            {biographyBlocks?.map((paragraph, index) => renderRichParagraph(paragraph, index))}
            {selectedPerson.wikiUrl && (
              <TouchableOpacity
                style={styles.wikiLink}
                onPress={() => Linking.openURL(selectedPerson.wikiUrl)}
                activeOpacity={0.7}
              >
                <Text style={styles.wikiLinkText}>Read full article on Wikipedia →</Text>
              </TouchableOpacity>
            )}
            <Text style={styles.bioSource}>Source: Wikipedia</Text>
          </View>
        )}
      </ScrollView>
    );
  };

  // Render search results list
  const renderSearchResults = () => {
    if (searching) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.statusText}>Searching...</Text>
        </View>
      );
    }

    if (searchQuery.trim().length >= 2 && results.length === 0 && !searching) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.statusText}>No results found</Text>
          <Text style={styles.statusSubtext}>Try a different name or spelling</Text>
        </View>
      );
    }

    if (results.length === 0 && searchQuery.trim().length < 2) {
      return (
        <View style={styles.suggestedContainer}>
          <Text style={styles.suggestedTitle}>Suggested People</Text>
          <Text style={styles.suggestedSubtext}>
            Tap to view their dates for gematria calculations
          </Text>
          {SUGGESTED_PEOPLE.map((person) => (
            <TouchableOpacity
              key={person.id}
              style={styles.resultItem}
              onPress={() => handleSelectPerson(person)}
              activeOpacity={0.7}
              accessibilityLabel={`${person.name}: ${person.description}`}
            >
              <View style={styles.resultContent}>
                <Text style={styles.resultName}>{person.name}</Text>
                <Text style={styles.resultDescription}>
                  {person.description}
                </Text>
              </View>
              <Text style={styles.chevron}>›</Text>
            </TouchableOpacity>
          ))}
        </View>
      );
    }

    return (
      <View style={styles.suggestedContainer}>
        {results.map((person) => (
          <TouchableOpacity
            key={person.id}
            style={styles.resultItem}
            onPress={() => handleSelectPerson(person)}
            activeOpacity={0.7}
            accessibilityLabel={`${person.name}: ${person.description}`}
          >
            <View style={styles.resultContent}>
              <Text style={styles.resultName}>{person.name}</Text>
              {person.description ? (
                <Text style={styles.resultDescription}>
                  {person.description}
                </Text>
              ) : null}
            </View>
            <Text style={styles.chevron}>›</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // Loading overlay for detail fetch
  if (loadingDetails) {
    return (
      <View style={styles.pageContainer}>
        <Helmet>
          <title>Famous People - Gematria Calculator</title>
          <meta name="description" content="Search famous people and view their biographical dates for gematria calculations." />
        </Helmet>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.statusText}>Loading person details...</Text>
        </View>
      </View>
    );
  }

  // If a person is selected, show their detail view
  if (selectedPerson) {
    return (
      <View style={styles.pageContainer}>
        <Helmet>
          <title>{selectedPerson.name} - Famous People - Gematria Calculator</title>
          <meta name="description" content={`View biographical dates and details for ${selectedPerson.name} for gematria calculations.`} />
        </Helmet>
        {renderPersonDetail()}
      </View>
    );
  }

  // Default: search view
  return (
    <View style={styles.pageContainer}>
      <Helmet>
        <title>Famous People - Gematria Calculator</title>
        <meta name="description" content="Search famous people and view their biographical dates for gematria calculations." />
      </Helmet>

      <Text style={styles.pageTitle}>Famous People</Text>
      <Text style={styles.pageSubtitle}>
        Search for historical and contemporary figures to explore their dates and biographical details.
      </Text>

      {/* Search Input */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search famous people..."
          placeholderTextColor={colors.textSecondary}
          value={searchQuery}
          onChangeText={handleSearchChange}
          autoCorrect={false}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => handleSearchChange('')} style={styles.clearButton}>
            <Text style={styles.clearButtonText}>×</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Results */}
      {renderSearchResults()}
    </View>
  );
}

const createStyles = (colors, isMobile) =>
  StyleSheet.create({
    pageContainer: {
      flex: 1,
      padding: isMobile ? 16 : 24,
      maxWidth: 800,
      width: '100%',
      alignSelf: 'center',
    },
    pageTitle: {
      fontSize: 28,
      fontWeight: '700',
      color: colors.text,
      marginBottom: 8,
    },
    pageSubtitle: {
      fontSize: 15,
      color: colors.textSecondary,
      marginBottom: 20,
      lineHeight: 22,
    },
    searchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 8,
      paddingHorizontal: 12,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: colors.border,
    },
    searchInput: {
      flex: 1,
      paddingVertical: 12,
      fontSize: 15,
      color: colors.text,
      outlineStyle: 'none',
    },
    clearButton: {
      padding: 6,
    },
    clearButtonText: {
      fontSize: 20,
      color: colors.textSecondary,
      fontWeight: '700',
    },
    centerContainer: {
      paddingVertical: 60,
      alignItems: 'center',
    },
    statusText: {
      fontSize: 15,
      color: colors.textSecondary,
      marginTop: 12,
      textAlign: 'center',
    },
    statusSubtext: {
      fontSize: 13,
      color: colors.textSecondary,
      marginTop: 4,
      textAlign: 'center',
    },
    suggestedContainer: {
      paddingBottom: 24,
    },
    suggestedTitle: {
      fontSize: 20,
      fontWeight: '700',
      color: colors.text,
      marginBottom: 4,
    },
    suggestedSubtext: {
      fontSize: 13,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    resultItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 14,
      backgroundColor: colors.surface,
      borderRadius: 8,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: colors.border,
      cursor: 'pointer',
    },
    resultContent: {
      flex: 1,
    },
    resultName: {
      fontSize: 15,
      fontWeight: '600',
      color: colors.text,
    },
    resultDescription: {
      fontSize: 13,
      color: colors.textSecondary,
      marginTop: 2,
    },
    chevron: {
      fontSize: 22,
      color: colors.textSecondary,
      marginLeft: 8,
    },

    // Detail view
    detailContainer: {
      flex: 1,
    },
    backButton: {
      marginBottom: 16,
      cursor: 'pointer',
    },
    backText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: '600',
    },
    personName: {
      fontSize: 28,
      fontWeight: '700',
      color: colors.text,
      marginBottom: 4,
    },
    personDescription: {
      fontSize: 15,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    occupations: {
      fontSize: 13,
      color: colors.primary,
      marginBottom: 20,
    },

    // Date cards
    datesContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 12,
      marginBottom: 20,
    },
    dateCard: {
      flex: isMobile ? undefined : 1,
      padding: 16,
      borderRadius: 10,
      borderWidth: 1.5,
    },
    dobCard: {
      backgroundColor: colors.surface,
      borderColor: colors.primary,
    },
    dodCard: {
      backgroundColor: colors.surface,
      borderColor: colors.border,
    },
    dateCardLabel: {
      fontSize: 11,
      fontWeight: '700',
      color: colors.textSecondary,
      letterSpacing: 1,
      marginBottom: 4,
    },
    dateCardValue: {
      fontSize: 17,
      fontWeight: '700',
      color: colors.text,
    },

    // Section titles
    sectionTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: colors.text,
      marginBottom: 8,
    },

    // Timeline
    timelineSection: {
      marginBottom: 24,
    },
    timelineItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      minHeight: 40,
      paddingVertical: 6,
      paddingHorizontal: 6,
      marginBottom: 4,
      backgroundColor: colors.surface,
      borderRadius: 6,
    },
    timelineDotColumn: {
      width: 24,
      alignItems: 'center',
    },
    timelineDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.border,
      marginTop: 5,
    },
    timelineDotDob: {
      backgroundColor: colors.primary,
      width: 12,
      height: 12,
      borderRadius: 6,
      marginTop: 4,
    },
    timelineDotDod: {
      backgroundColor: '#F44336',
      width: 12,
      height: 12,
      borderRadius: 6,
      marginTop: 4,
    },
    timelineLine: {
      width: 2,
      flex: 1,
      backgroundColor: colors.border,
      marginTop: 4,
    },
    timelineContent: {
      flex: 1,
      paddingLeft: 10,
      paddingBottom: 10,
    },
    timelineDate: {
      fontSize: 13,
      fontWeight: '700',
      color: colors.primary,
      marginBottom: 2,
    },
    timelineDateHighlight: {
      fontSize: 14,
      fontWeight: '700',
    },
    timelineEvent: {
      fontSize: 13,
      color: colors.text,
      lineHeight: 18,
    },

    // Biography
    bioSection: {
      marginBottom: 40,
    },
    languageSection: {
      marginBottom: 16,
    },
    languageHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    languageLabel: {
      flex: 1,
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: '600',
    },
    languageList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    languageChip: {
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      cursor: 'pointer',
    },
    languageChipActive: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },
    languageChipText: {
      fontSize: 13,
      color: colors.text,
      fontWeight: '600',
    },
    languageChipTextActive: {
      color: '#fff',
    },
    bioText: {
      fontSize: 15,
      color: colors.text,
      lineHeight: 24,
      marginBottom: 12,
    },
    bioTextRtl: {
      textAlign: 'right',
    },
    bioBold: {
      fontWeight: '700',
    },
    bioItalic: {
      fontStyle: 'italic',
    },
    bioSource: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 12,
      fontStyle: 'italic',
    },
    wikiLink: {
      marginTop: 16,
      paddingVertical: 10,
      paddingHorizontal: 14,
      backgroundColor: colors.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.primary,
      alignSelf: 'flex-start',
      cursor: 'pointer',
    },
    wikiLinkText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: '600',
    },
  });
